import { Album } from '@/models/album.ts'
import { QRCodeShape } from '@/components/QRCodeDesigner.tsx'
import { DriveUserRole, DriveUserRoles } from '@/models/driveUserRole.ts'
import { HeyProfile } from '@/models/profile.ts'
import { LayoutMode } from '@/components/Files/FilesListing.tsx'
import { DownloadOptions } from '@/models/downloadOptions.ts'

export enum HeyDriveType {
  WEDDING = 'wedding',
  BIRTHDAY = 'birthday',
  PARTY = 'party',
  BABY_SHOWER = 'baby_shower',
  COMPANY = 'company',
  PROF = 'prof'
  // NEW_YEAR = 'new_year',
  // CHRISTMAS = 'christmas',
  // VACATION = 'vacation',
}

export enum HeyDriveThemeName {
  DEFAULT = '',
  GOLDEN_HOUR = 'golden_hour',
  NARROW = 'narrow',
  EDITORIAL = 'editorial',
  MAGAZINE = 'magazine',
  MINIMAL = 'minimal',
  FLOATING = 'floating',
  OVERLAY = 'overlay',
  GRAND = 'grand',
  TWILIGHT = 'twilight',
  DUSK = 'dusk',
  WINE_NIGHT = 'wine_night',
  BLUSHING = 'blushing',
  BLOOM_ROSE = 'bloom_rose',
  BLOOM_CIEL = 'bloom_ciel',
  BLOOM_HONEY = 'bloom_honey',
  BLOOM_SAGE = 'bloom_sage',
}

/**
 * Available header_style values:
 *
 * - 'centered'   – Image centered with "Fotos von" rotated on left, title + date on right
 * - 'narrow'     – Like centered but image is narrow/portrait in the center
 * - 'partial'    – Image ~65% left with padding, title section overlays from right with neutral bg
 * - 'botanical'  – Fullscreen image, decorative leaves, neutral card with title at bottom
 * - 'editorial'  – Left text column (title, date, contact), right image taking ~60%
 * - 'magazine'   – Dark overlay + white border, large stacked white title centered
 * - 'minimal'    – Image fills viewport (no darkening), small text bottom-left
 * - 'floating'   – Dark overlay + white border, floating neutral card at bottom center
 * - 'overlay'    – Dark overlay + white border, right-aligned neutral card overlay
 * - 'grand'      – Large image prominent center, small text above, title below
 */

export interface DriveThemeStyleDefinition {
  theme_name: string
  theme_display_name: string
  color_primary: string | null
  color_neutral: string | null
  color_content: string | null

  header_style: string | null
  layout_mode: LayoutMode | null
  album_overview: 'both' | 'desktop' | 'mobile' | 'none' | null
  album_overview_style: 'rect' | 'circle' | null
  button_shape: 'rounded' | 'rect' | null
  button_variant: 'outline' | 'filled' | null
  font_header: string | null
  font_content: string | null
  are_elements_contained: boolean | null

  spacing: 0 | 5 | 10 | 15 | 20 | null
  show_contact: boolean | null
}

const GOLDEN_HOUR_THEME: DriveThemeStyleDefinition = {
  theme_name: HeyDriveThemeName.GOLDEN_HOUR,
  theme_display_name: 'Golden Hour',
  color_primary: '#000000',
  color_neutral: '#FFFFFF',
  color_content: '#0F0F0F',
  header_style: 'centered',
  layout_mode: 'masonry',
  album_overview: 'both',
  album_overview_style: 'rect',
  button_shape: 'rect',
  button_variant: 'outline',
  font_header: 'cormorant-infant',
  font_content: 'poppins',
  are_elements_contained: false,
  spacing: 15,
  show_contact: false,
}

const ThemeDefinitions: DriveThemeStyleDefinition[] = [
  {
    ...GOLDEN_HOUR_THEME,
    theme_name: HeyDriveThemeName.DEFAULT,
  },
  GOLDEN_HOUR_THEME,
  {
    ...GOLDEN_HOUR_THEME,
    theme_name: HeyDriveThemeName.NARROW,
    theme_display_name: 'Silhouette',
    header_style: 'narrow',
    layout_mode: 'rows',
    album_overview: 'both',
    album_overview_style: 'rect',
  },
  {
    ...GOLDEN_HOUR_THEME,
    theme_name: HeyDriveThemeName.EDITORIAL,
    theme_display_name: 'Chronicle',
    header_style: 'editorial',
    layout_mode: 'rows',
    album_overview: 'both',
    album_overview_style: 'rect',
  },
  {
    ...GOLDEN_HOUR_THEME,
    theme_name: HeyDriveThemeName.MAGAZINE,
    theme_display_name: 'Spotlight',
    header_style: 'magazine',
    layout_mode: 'rows',
    album_overview: 'both',
    album_overview_style: 'rect',
  },
  {
    ...GOLDEN_HOUR_THEME,
    theme_name: HeyDriveThemeName.MINIMAL,
    theme_display_name: 'Essence',
    header_style: 'minimal',
    layout_mode: 'rows',
    album_overview: 'both',
    album_overview_style: 'rect',
  },
  {
    ...GOLDEN_HOUR_THEME,
    theme_name: HeyDriveThemeName.FLOATING,
    theme_display_name: 'Gallery',
    header_style: 'floating',
    layout_mode: 'rows',
    album_overview: 'both',
    album_overview_style: 'rect',
  },
  {
    ...GOLDEN_HOUR_THEME,
    theme_name: HeyDriveThemeName.OVERLAY,
    theme_display_name: 'Noir',
    header_style: 'overlay',
    layout_mode: 'rows',
    album_overview: 'both',
    album_overview_style: 'rect',
  },
  {
    ...GOLDEN_HOUR_THEME,
    theme_name: HeyDriveThemeName.GRAND,
    theme_display_name: 'Statement',
    header_style: 'grand',
    layout_mode: 'rows',
    album_overview: 'both',
    album_overview_style: 'rect',
  },
  {
    theme_name: HeyDriveThemeName.DUSK,
    theme_display_name: 'Dusk',
    color_primary: '#FFFFFF',
    color_neutral: '#000000',
    color_content: '#FFFFFF',
    header_style: 'magazine',
    layout_mode: 'rows',
    album_overview: 'both',
    album_overview_style: 'rect',
    button_shape: 'rounded',
    button_variant: null,
    font_header: 'bonny',
    font_content: 'gambetta',
    are_elements_contained: true,
    spacing: 5,
    show_contact: true,
  },
  {
    theme_name: HeyDriveThemeName.TWILIGHT,
    theme_display_name: 'Twilight',
    color_primary: '#000000',
    color_neutral: '#FFFFFF',
    color_content: '#1c1c1c',
    header_style: 'partial',
    layout_mode: 'columns',
    album_overview: 'both',
    album_overview_style: 'rect',
    button_shape: 'rect',
    button_variant: 'outline',
    font_header: 'melodrama',
    font_content: 'bespoke-serif',
    are_elements_contained: false,
    spacing: 10,
    show_contact: true,
  },
  {
    theme_name: HeyDriveThemeName.WINE_NIGHT,
    theme_display_name: 'Wine Night',
    color_primary: '#781140',
    color_neutral: '#f5f1ef',
    color_content: '#040404',
    header_style: 'partial',
    layout_mode: 'rows',
    album_overview: 'both',
    album_overview_style: 'rect',
    button_shape: 'rounded',
    button_variant: null,
    font_header: 'gambarino',
    font_content: 'satoshi',
    are_elements_contained: true,
    spacing: 5,
    show_contact: true,
  },
  {
    theme_name: HeyDriveThemeName.BLUSHING,
    theme_display_name: 'Blushing',
    color_primary: '#C7858E',
    color_neutral: '#FCFBF7',
    color_content: '#030303',
    header_style: 'centered',
    layout_mode: 'masonry',
    album_overview: 'both',
    album_overview_style: 'circle',
    button_shape: 'rounded',
    button_variant: null,
    font_header: 'britney',
    font_content: 'general-sans',
    are_elements_contained: true,
    spacing: 10,
    show_contact: true,
  },
  {
    theme_name: HeyDriveThemeName.BLOOM_ROSE,
    theme_display_name: 'Bloom Rosé',
    color_primary: '#C7858E',
    color_neutral: '#FCFBF7',
    color_content: '#030303',
    header_style: 'botanical',
    layout_mode: 'masonry',
    album_overview: 'both',
    album_overview_style: 'circle',
    button_shape: 'rounded',
    button_variant: null,
    font_header: 'bonny',
    font_content: 'nunito',
    are_elements_contained: true,
    spacing: 10,
    show_contact: true,
  },
  {
    theme_name: HeyDriveThemeName.BLOOM_CIEL,
    theme_display_name: 'Bloom Ciel',
    color_primary: '#7BA7BC',
    color_neutral: '#F0F5F7',
    color_content: '#040404',
    header_style: 'botanical',
    layout_mode: 'masonry',
    album_overview: 'both',
    album_overview_style: 'circle',
    button_shape: 'rounded',
    button_variant: null,
    font_header: 'bonny',
    font_content: 'nunito',
    are_elements_contained: true,
    spacing: 10,
    show_contact: true,
  },
  {
    theme_name: HeyDriveThemeName.BLOOM_HONEY,
    theme_display_name: 'Bloom Honey',
    color_primary: '#EECA89',
    color_neutral: '#FAF5CE',
    color_content: '#040404',
    header_style: 'botanical',
    layout_mode: 'masonry',
    album_overview: 'both',
    album_overview_style: 'circle',
    button_shape: 'rounded',
    button_variant: null,
    font_header: 'bonny',
    font_content: 'nunito',
    are_elements_contained: true,
    spacing: 10,
    show_contact: true,
  },
  {
    theme_name: HeyDriveThemeName.BLOOM_SAGE,
    theme_display_name: 'Bloom Sage',
    color_primary: '#5A7447',
    color_neutral: '#E6F0DD',
    color_content: '#040404',
    header_style: 'botanical',
    layout_mode: 'masonry',
    album_overview: 'both',
    album_overview_style: 'circle',
    button_shape: 'rounded',
    button_variant: null,
    font_header: 'bonny',
    font_content: 'nunito',
    are_elements_contained: true,
    spacing: 10,
    show_contact: true,
  },
]

function buildThemeMap(): Record<string, DriveThemeStyleDefinition> {
  return ThemeDefinitions.reduce((acc, theme) => {
    acc[theme.theme_name] = theme
    return acc
  }, {} as Record<string, DriveThemeStyleDefinition>)
}

const ThemeDefinitionsByName = buildThemeMap()

export interface DriveContact {
  company: string
  first_name: string
  last_name: string
  website: string
  email: string
  phone: string
  facebook: string
  instagram: string
  twitter: string
  tiktok: string
  pinterest: string
  youtube: string
  google: string
  image: string
}

/**
 * Caption rendered below an opened (fullscreen) image.
 *
 * `''` keeps the per-drive-type default (normal drives: uploader + date, prof drives: nothing),
 * every other value is an explicit override that applies to both drive types.
 * See `buildOpenedImageCaption()` in `models/albumFile.ts`.
 */
export type OpenedImageCaption = '' | 'filename' | 'date' | 'filename,date'

export interface HeyDrive {
  id: string
  drive_type: HeyDriveType
  created_at: string
  title: string
  author: string
  albums: Array<Album>
  color_primary: string | null
  color_neutral: string | null
  color_content: string | null
  font: string
  font_content: string
  is_demo: boolean
  is_hide_yes: boolean
  is_show_onboarding: boolean
  is_ha: boolean
  ad_mode: string | null
  qr_code_image: string | null
  qr_fg: string
  qr_bg: string
  qr_shape: QRCodeShape
  custom_logo: string | null
  usage_starts_at: string | null
  // expires_at is until when the drive is available/bought for. It is not necessarily the deletion date.
  expires_at: string | null
  // user_defined_expires_at is the latest expiration field. It's primarily used for professional drives so the drive is disabled for user access.
  // It is not associated with deletion yet - only access removal indicator.
  user_defined_expires_at: string | null
  default_files_order: '' | 'created-at' | '-created-at' | 'file-creation' | '-file-creation'
  use_details_dl: boolean
  // will_be_deleted_at is when the drive should actually be deleted from storage. That's usually expires_at + 30 days
  will_be_deleted_at: string | null
  event_date: string | null
  // is_deletion_state should be set after the expires_at was passed. This means it's the time between expires_at and will_be_deleted_at
  is_deletion_state: boolean
  user_roles: Array<DriveUserRole>
  theme_name: HeyDriveThemeName | string
  spacing: number | null
  layout_mode: LayoutMode | null
  album_overview: 'both' | 'desktop' | 'mobile' | 'none' | null
  album_overview_style: 'rect' | 'circle' | null
  are_elements_contained: boolean | null
  contact: DriveContact | null
  // child drives are usually guest drives for prof drives. So the customer has an additional guest gallery
  // where guests upload their images/videos. This child drive has the parent_drive set, OneToOneField. Both are IDs.
  child_drive: string | null
  parent_drive: string | null
  is_hide_selection: boolean
  is_hide_download: boolean
  is_hide_share: boolean
  watermark: string | null
  is_selection_name_required: boolean
  is_selection_email_required: boolean
  is_selection_message_required: boolean
  // Caption shown below an opened (fullscreen) image. '' = none (default).
  // Storage only, no backend behaviour — the frontend decides what to render.
  opened_image_caption: OpenedImageCaption
  // Which download resolutions the gallery offers. '' = all (default).
  // Storage only, no backend behaviour — resolve it via `resolveDownloadQualities()`.
  download_options: DownloadOptions
  // computed by backend, not settable atm
  is_hide_shop: boolean
}

/**
 * Resolves a theme definition from a theme name and drive type.
 * For prof drives, falls back to the DEFAULT theme when theme_name is empty/unknown.
 * Single source of truth — use this everywhere instead of accessing ThemeDefinitionsByName directly.
 */
export function resolveTheme(themeName: string | null | undefined, driveType: string | null | undefined): DriveThemeStyleDefinition | null {
  if (driveType === HeyDriveType.PROF) {
    return (themeName ? ThemeDefinitionsByName[themeName] : null) ?? ThemeDefinitionsByName[HeyDriveThemeName.DEFAULT] ?? null
  }
  return themeName ? (ThemeDefinitionsByName[themeName] ?? null) : null
}

export function getThemeDefinitionForDrive(drive: HeyDrive): DriveThemeStyleDefinition | null {
  return resolveTheme(drive.theme_name, drive.drive_type)
}

export { ThemeDefinitions, ThemeDefinitionsByName }

/**
 * Scroll indicator appearance per device for each header style.
 * 'light' = white with drop-shadow (for dark backgrounds)
 * 'dark' = default content color (for light backgrounds)
 * 'hidden' = not shown
 */
export type ScrollIndicatorAppearance = 'light' | 'dark' | 'hidden'

export interface ScrollIndicatorConfig {
  desktop: ScrollIndicatorAppearance
  mobile: ScrollIndicatorAppearance
}

const scrollIndicatorConfigs: Record<string, ScrollIndicatorConfig> = {
  centered:   { desktop: 'dark',   mobile: 'hidden' },
  narrow:     { desktop: 'dark',   mobile: 'hidden' },
  partial:    { desktop: 'dark',   mobile: 'light' },
  botanical:  { desktop: 'light',  mobile: 'light' },
  editorial:  { desktop: 'light',  mobile: 'hidden' },
  magazine:   { desktop: 'light',  mobile: 'light' },
  minimal:    { desktop: 'light',  mobile: 'hidden' },
  floating:   { desktop: 'dark',   mobile: 'hidden' },
  overlay:    { desktop: 'light',  mobile: 'hidden' },
  grand:      { desktop: 'hidden', mobile: 'hidden' },
}

export function getScrollIndicatorConfig(headerStyle: string | null | undefined): ScrollIndicatorConfig {
  if (!headerStyle) return { desktop: 'dark', mobile: 'dark' }
  return scrollIndicatorConfigs[headerStyle] ?? { desktop: 'dark', mobile: 'dark' }
}

export function shouldUseGalleryNav(drive: HeyDrive): boolean {
  return drive.drive_type === HeyDriveType.PROF
}

// Returns true when the user-defined expiration date has passed. This is a plain date
// field (no time), so access remains valid through the end of the expiry day. Primarily
// used for professional drives to indicate that user access has expired.
export function isDriveAccessExpired(userDefinedExpiresAt: string | null | undefined): boolean {
  if (!userDefinedExpiresAt) {
    return false
  }
  const expiry = new Date(`${userDefinedExpiresAt}T23:59:59`)
  if (isNaN(expiry.getTime())) {
    return false
  }
  return expiry.getTime() < Date.now()
}

export function hasDrivePermission(drive: HeyDrive, user: HeyProfile, role: DriveUserRoles): boolean {
  if (drive.author && drive.author === user.username) {
    return true;
  }

  if (drive.user_roles?.some(r => r.role === DriveUserRoles.ADMIN && r.user === user.username)) {
    return true;
  }

  return drive.user_roles?.some(r => r.role === role && r.user === user.username) ?? false;
}
